// Block Info — toggle each item between its masked image and its WYSIWYG text.
//
// The decorative SVG mask (uploaded per item, exposed as the CSS custom prop
// `--block-info-mask`) is turned into an inline <clipPath> so GSAP MorphSVGPlugin
// can animate its shape. Clicking an item's button morphs that item's mask into
// a square (revealing the text inside the squared shape); any other open item
// morphs back to its original mask. The image/text cross-fade still lives in CSS
// (assets/src/scss/blocks/block-info.scss) driven by the `.open` class.

import { gsap } from "gsap";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin.js";
import { stringToRawPath, rawPathToString, transformRawPath } from "gsap/utils/paths.js";

gsap.registerPlugin(MorphSVGPlugin);

const SVG_NS = "http://www.w3.org/2000/svg";

// Target shape: a full unit square in objectBoundingBox space (0..1).
const SQUARE_PATH = "M0,0 L1,0 L1,1 L0,1 Z";

const MORPH = { duration: 0.6, ease: "power2.inOut" };

// Pull the mask file URL out of the `--block-info-mask` custom property the
// Twig template sets on `.block-info__media` (e.g. `url('…/shape.svg')`).
function getMaskUrl(media) {
    const value = getComputedStyle(media).getPropertyValue("--block-info-mask").trim();
    if (!value || value === "none") return null;
    const match = value.match(/url\(\s*['"]?(.*?)['"]?\s*\)/i);
    return match ? match[1] : null;
}

// Fetch the uploaded mask SVG and return a single path `d` string normalized to
// objectBoundingBox space (0..1 on both axes) so it can clip the square media
// box regardless of the media's pixel size — and so it shares MorphSVG's
// coordinate space with SQUARE_PATH.
async function loadNormalizedMask(url) {
    const res = await fetch(url);
    if (!res.ok) return null;

    const doc = new DOMParser().parseFromString(await res.text(), "image/svg+xml");
    const svg = doc.querySelector("svg");
    if (!svg || doc.querySelector("parsererror")) return null;

    // Resolve the coordinate box from the viewBox (preferred) or width/height.
    let minX = 0, minY = 0, width = 0, height = 0;
    const viewBox = svg.getAttribute("viewBox");
    if (viewBox) {
        [minX, minY, width, height] = viewBox.split(/[\s,]+/).map(Number);
    } else {
        width = parseFloat(svg.getAttribute("width")) || 0;
        height = parseFloat(svg.getAttribute("height")) || 0;
    }
    if (!width || !height) return null;

    // Collect every drawable shape, converting non-path shapes to paths so the
    // mask works whether it was authored as <path>, <circle>, <polygon>, etc.
    let d = "";
    svg.querySelectorAll("path, circle, ellipse, rect, polygon, polyline, line").forEach((shape) => {
        let pathEl = shape;
        if (shape.tagName.toLowerCase() !== "path") {
            try {
                pathEl = MorphSVGPlugin.convertToPath(shape)[0] || null;
            } catch (e) {
                pathEl = null;
            }
        }
        const dd = pathEl && pathEl.getAttribute("d");
        if (dd) d += dd + " ";
    });
    if (!d.trim()) return null;

    // Scale from the SVG's user space into the 0..1 objectBoundingBox space:
    // x -> (x - minX) / width, y -> (y - minY) / height.
    const rawPath = transformRawPath(
        stringToRawPath(d),
        1 / width, 0, 0, 1 / height,
        -minX / width, -minY / height
    );
    return rawPathToString(rawPath);
}

// Build an inline <clipPath> for the normalized mask and apply it to the media,
// disabling the CSS mask fallback now that the morphable clip is in charge.
function applyClip(media, normalizedD, id) {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", "0");
    svg.setAttribute("height", "0");
    svg.setAttribute("aria-hidden", "true");
    svg.style.position = "absolute";

    const defs = document.createElementNS(SVG_NS, "defs");
    const clipPath = document.createElementNS(SVG_NS, "clipPath");
    clipPath.setAttribute("id", id);
    clipPath.setAttribute("clipPathUnits", "objectBoundingBox");

    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", normalizedD);

    clipPath.appendChild(path);
    defs.appendChild(clipPath);
    svg.appendChild(defs);
    media.appendChild(svg);

    media.style.clipPath = `url(#${id})`;
    media.style.webkitClipPath = `url(#${id})`;
    // The CSS `mask-image` was only the no-JS fallback shape; the clip-path now
    // provides the (morphable) shape, so drop the static mask to avoid stacking.
    media.style.maskImage = "none";
    media.style.webkitMaskImage = "none";

    return path;
}

async function initItem(item, id) {
    const media = item.querySelector(".block-info__media");
    if (!media) return null;

    const url = getMaskUrl(media);
    if (!url) return null;

    const normalizedD = await loadNormalizedMask(url);
    if (!normalizedD) return null;

    const path = applyClip(media, normalizedD, id);
    return { path, originalD: normalizedD };
}

document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".block-info").forEach((block, blockIndex) => {
        const items = Array.from(block.querySelectorAll(".block-info__item"));
        if (!items.length) return;

        items.forEach(async (item, itemIndex) => {
            const btn = item.querySelector(".block-info__btn");
            if (!btn) return;

            // Kick off mask setup (async); morph state lives on the item node so
            // the click handler can read it once loading resolves.
            item._infoMask = await initItem(item, `block-info-mask-${blockIndex}-${itemIndex}`);

            btn.addEventListener("click", () => {
                // Clicking the already-open item closes it; otherwise open it and
                // close any other open item.
                const willOpen = !item.classList.contains("open");

                items.forEach((other) => {
                    const isTarget = willOpen && other === item;
                    other.classList.toggle("open", isTarget);

                    const mask = other._infoMask;
                    if (!mask) return;
                    gsap.to(mask.path, {
                        ...MORPH,
                        morphSVG: isTarget ? SQUARE_PATH : mask.originalD,
                    });
                });
            });
        });
    });
});
